import type { Core } from "@/client/core/Core.api";
import { createSvgIcon } from "@/client/ui/design-system/icons";
import { colors } from "@/client/ui/design-system/styles/colors.css";
import { createEl, resizeCanvas2D } from "@/client/utils/createEl";
import { getSpliceSampleDisplayName } from "@/client/utils/createSpliceSampleInputFile";
import { decodeAudioFile } from "@/client/utils/decodeAudio";
import { getChannelData } from "@/client/utils/getChannelData";
import { createScalableWaveform, type ScalableWaveform } from "@/client/utils/waveform";
import { formatSampleDuration, formatSampleKey } from "./sample-format";
import { getSeedPlaybackId } from "./sounds-browser-actions";
import type { SoundsBrowserContext, SoundsBrowserSeed } from "./sounds-browser.api";
import * as styles from "./sounds-browser.css";

const kWaveformHeight_px = 40;
const kWaveformMaxWidth_px = 1024;

export interface SeedCard {
    readonly el: HTMLElement;
    /** Shows this sample, or nothing. */
    readonly setSeed: (seed: SoundsBrowserSeed | undefined) => void;
}

interface SeedView {
    readonly name: string;
    readonly artworkUrl: string;
    readonly isCatalog: boolean;
    readonly bpm: number | null;
    readonly key: string;
    readonly chordType: string;
    readonly duration_ms: number | undefined;
}

/**
 * The sample in the Search-with-Sound zone, drawn the way the shared dropzone
 * draws a loaded file: artwork, name and metadata on one row, play and a
 * waveform with progress on the next. The same thing the user sees in every
 * app's input, so the zone reads as an input rather than a chip.
 */
export const createSeedCard = (
    core: Core,
    context: SoundsBrowserContext,
    onClear: () => void,
): SeedCard => {
    const { actions, signals, previews, player } = context;
    let seed: SoundsBrowserSeed | undefined;
    let waveform: ScalableWaveform | undefined;
    let drawnWidth = 0;
    let isPlayingThis = false;
    let rafId: number | undefined;
    /** Which seed the waveform in hand belongs to, so a late decode is not drawn over the next sample. */
    let loadToken = 0;

    const toView = (current: SoundsBrowserSeed): SeedView => {
        if (current.kind === "catalog") {
            const { metadata } = current.result;
            return {
                name: getSpliceSampleDisplayName(metadata.name),
                artworkUrl: metadata.packArtworkUrl,
                isCatalog: true,
                bpm: metadata.bpm > 0 ? metadata.bpm : null,
                key: metadata.key,
                chordType: metadata.chordType,
                duration_ms: metadata.duration,
            };
        }
        const resource = core.actions.getAudioResource(current.id);
        return {
            name: current.name,
            artworkUrl: resource?.metadata.artworkUrl ?? "",
            isCatalog: false,
            bpm: current.bpm,
            key: resource?.metadata.key ?? "",
            chordType: resource?.metadata.chordType ?? "",
            duration_ms:
                resource === undefined
                    ? undefined
                    : Math.round(resource.metadata.duration_s * 1000),
        };
    };

    // ── Info row ─────────────────────────────────────────────────────────────
    const artEl = createEl("div", styles.seedArt, []);
    const nameEl = createEl("h3", styles.seedFilename, "");
    const metaEl = createEl("div", styles.seedMetaRow, []);
    const clearButton = createEl(
        "button",
        styles.seedClearButton,
        [createSvgIcon("trash", styles.seedClearIcon)],
        (el) => {
            el.type = "button";
            el.setAttribute("aria-label", "Clear the sample being searched from");
            el.addEventListener("click", onClear);
        },
    );
    const infoRow = createEl("div", styles.seedInfoRow, [
        artEl,
        createEl("div", styles.seedInfoColumn, [nameEl, metaEl]),
        clearButton,
    ]);

    // ── Waveform row ─────────────────────────────────────────────────────────
    const playIcon = createSvgIcon("playSolid", styles.seedPlayIcon);
    const stopIcon = createSvgIcon("stopSolid", styles.seedPlayIcon);
    const playButton = createEl("button", styles.seedPlayButton, [playIcon], (el) => {
        el.type = "button";
        el.setAttribute("aria-label", "Play the sample being searched from");
        el.addEventListener("click", actions.toggleSeedPreview);
    });
    const canvas = createEl("canvas", styles.waveformCanvas);
    const ctx = canvas.getContext("2d");
    const placeholderEl = createEl("span", styles.waveformPlaceholder);
    const waveformEl = createEl("div", styles.seedWaveform, [placeholderEl, canvas], (el) => {
        el.addEventListener("click", actions.toggleSeedPreview);
    });
    const waveformRow = createEl("div", styles.seedWaveformRow, [playButton, waveformEl]);

    const el = createEl("div", styles.seedCard, [infoRow, waveformRow]);

    // ── Drawing ──────────────────────────────────────────────────────────────
    const draw = (): void => {
        if (ctx === null || waveform === undefined) {
            return;
        }
        const width = Math.floor(waveformEl.clientWidth);
        if (width <= 0) {
            return;
        }
        if (width !== drawnWidth) {
            drawnWidth = width;
            resizeCanvas2D(canvas, ctx, width, kWaveformHeight_px, "100%", "100%");
        }
        placeholderEl.style.display = "none";
        const path = waveform.getPath(width, kWaveformHeight_px, true);
        ctx.clearRect(0, 0, width, kWaveformHeight_px);
        ctx.fillStyle = colors.waveformFill;
        ctx.fill(path);
        const progress = isPlayingThis ? player.getProgress_0to1() : 0;
        if (progress > 0) {
            ctx.save();
            ctx.beginPath();
            ctx.rect(0, 0, width * progress, kWaveformHeight_px);
            ctx.clip();
            ctx.fillStyle = colors.waveformProgress;
            ctx.fill(path);
            ctx.restore();
        }
    };

    const stopTicker = (): void => {
        if (rafId !== undefined) {
            cancelAnimationFrame(rafId);
            rafId = undefined;
        }
    };
    const tick = (): void => {
        draw();
        rafId = requestAnimationFrame(tick);
    };

    const setPlaying = (isPlaying: boolean): void => {
        isPlayingThis = isPlaying;
        playButton.replaceChildren(isPlaying ? stopIcon : playIcon);
        playButton.setAttribute(
            "aria-label",
            isPlaying
                ? "Stop the sample being searched from"
                : "Play the sample being searched from",
        );
        stopTicker();
        if (isPlaying) {
            tick();
        } else {
            draw();
        }
    };

    signals.playback.listen((playback) => {
        setPlaying(
            seed !== undefined && playback.isPlaying && playback.uuid === getSeedPlaybackId(seed),
        );
    });

    const loadWaveform = async (
        current: SoundsBrowserSeed,
    ): Promise<ScalableWaveform | undefined> => {
        if (current.kind === "catalog") {
            const preview = await previews.ensure(current.result);
            return preview instanceof Error ? undefined : preview.waveform;
        }
        const resource = core.actions.getAudioResource(current.id);
        if (resource === undefined) {
            return undefined;
        }
        try {
            const audioBuffer = await decodeAudioFile(resource.file);
            return createScalableWaveform(getChannelData(audioBuffer), kWaveformMaxWidth_px);
        } catch {
            return undefined;
        }
    };

    const renderMeta = (view: SeedView): void => {
        const parts: string[] = [];
        if (view.bpm !== null) {
            parts.push(`${String(Math.round(view.bpm))} BPM`);
        }
        const key = formatSampleKey(view.key, view.chordType);
        if (key !== "--") {
            parts.push(key);
        }
        if (view.duration_ms !== undefined) {
            parts.push(formatSampleDuration(view.duration_ms));
        }
        metaEl.replaceChildren(
            ...parts.flatMap((part, index) => [
                ...(index === 0 ? [] : [createEl("span", styles.seedMetaSeparator, "·")]),
                createEl("span", styles.seedMetaText, part),
            ]),
        );
        metaEl.style.display = parts.length === 0 ? "none" : "";
    };

    const setSeed = (next: SoundsBrowserSeed | undefined): void => {
        seed = next;
        waveform = undefined;
        drawnWidth = 0;
        loadToken += 1;
        stopTicker();
        if (next === undefined) {
            return;
        }
        const view = toView(next);
        nameEl.textContent = view.name;
        nameEl.title = view.name;
        artEl.replaceChildren(
            view.artworkUrl !== ""
                ? createEl("img", "", [], (img) => {
                      img.src = view.artworkUrl;
                      img.alt = "";
                      img.draggable = false;
                  })
                : createSvgIcon(view.isCatalog ? "loop" : "userAudio", styles.artIcon),
        );
        renderMeta(view);
        placeholderEl.style.display = "";
        if (ctx !== null) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
        const playback = player.getState();
        setPlaying(playback.isPlaying && playback.uuid === getSeedPlaybackId(next));

        const token = loadToken;
        void loadWaveform(next).then((loaded) => {
            if (token !== loadToken || loaded === undefined) {
                return;
            }
            waveform = loaded;
            // The zone may still be laying out; draw on the next frame so the
            // canvas has its width.
            requestAnimationFrame(draw);
        });
    };

    return { el, setSeed };
};
